'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetchWithQuery } from '@/lib/api/apiClient';
import { Magazine, MagazinePage } from '@/types/magazines';
import { TaxRate } from '@/types/taxRate';
import { buildQueryParams } from '@/utils/helpers/queryHelper';
import ImageAdaptive from '@/components/ImageAdaptive';
import { NoData } from '@/components/NoData';
import ServiceHeader from '@/components/layout/service/ServiceHeader';
import MagazineDetailUnit from '@/(routers)/magazines/detail/[modelNumber]/components/unit/MagazineDetailUnit';

export interface MagazineDetailProps {
    paramModelNumber: string;
}

export default function MagazineDetail({ paramModelNumber }: MagazineDetailProps) {
    const router = useRouter();
    const { handleError, clearError } = useHandleError();
    const [isProcessing, setIsProcessing] = useState(false);
    const [magazine, setMagazine] = useState<Magazine>();
    const [magazinePages, setMagazinePages] = useState<MagazinePage[]>([]);
    const title = [magazine?.title_prefix, magazine?.title].filter(Boolean).join(' ') || undefined;
    const [taxRateData, setTaxRateData] = useState<TaxRate[] | null>(null);

    useEffect(() => {
        (async () => {
            try {
                clearError();
                setIsProcessing(true);
                const res = await apiFetchWithQuery(
                    `/magazines/detail`,
                    buildQueryParams({ model_number: paramModelNumber }),
                    {
                        method: 'GET',
                    },
                );
                if (!res?.magazine) {
                    setIsProcessing(false);
                    router.replace('/magazines/dive');
                    return;
                }
                setMagazine(res.magazine);
                setMagazinePages(res.magazine_pages);
                setTaxRateData(res.tax_rates);
            } catch (error: unknown) {
                handleError(error);
            } finally {
                setIsProcessing(false);
            }
        })();
    }, []);

    return (
        <>
            <ServiceHeader title="LINEUP." breadcrumbLeaf={title ?? ' '} />
            <div className="flex justify-center w-full pt-2">
                <div className="flex flex-col xl:flex-row justify-start xl:justify-center items-center xl:items-start w-full md:w-[98%] py-4">
                    {isProcessing ? (
                        <ImageAdaptive src="/images/common/loading.svg" baseWidth={40} breakpoints={{ md: 50 }} />
                    ) : (
                        <>
                            {magazine ? (
                                <>
                                    <motion.div className="flex flex-col justify-center items-center w-full h-auto">
                                        <MagazineDetailUnit
                                            magazine={magazine}
                                            magazine_pages={magazinePages}
                                            taxRates={taxRateData}
                                        />
                                    </motion.div>
                                </>
                            ) : (
                                <NoData message="該当する月刊みんなのダイブが見つかりませんでした。" />
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
